// ============================================================
// Patient Quality Assessment — content & scoring model
// All data is static and lives entirely on the client.
// ============================================================

export const QUESTIONS = [
  {
    id: "q1",
    number: "Q01",
    text: "What does your Meta campaign currently optimise for?",
    options: [
      { key: "A", label: "Form fills or lead form submissions", score: 0 },
      { key: "B", label: "Website visits or page clicks", score: 0 },
      { key: "C", label: "Consultation booked or demo completed", score: 2 },
      { key: "D", label: "Revenue events from CRM — closed procedures, paying patients", score: 3 },
    ],
  },
  {
    id: "q2",
    number: "Q02",
    text: "When you create a lookalike audience on Meta, what data do you seed it with?",
    options: [
      { key: "A", label: "We don't use lookalike audiences", score: 0 },
      { key: "B", label: "Website visitors or all form submissions", score: 1 },
      { key: "C", label: "All consultation bookings", score: 2 },
      { key: "D", label: "Patients who paid full price for premium procedures", score: 3 },
    ],
  },
  {
    id: "q3",
    number: "Q03",
    text: "What happens to a lead who fills your form but never picks up the phone?",
    options: [
      { key: "A", label: "Nothing specific — they stay in the system", score: 0 },
      { key: "B", label: "We call 2–3 times then move on", score: 1 },
      { key: "C", label: "Marked in CRM, but Meta keeps showing them ads", score: 1 },
      { key: "D", label: "They're sent to a suppression list — Meta stops targeting them", score: 3 },
    ],
  },
  {
    id: "q4",
    number: "Q04",
    text: "How do you measure whether your ads are working?",
    options: [
      { key: "A", label: "Number of leads generated", score: 0 },
      { key: "B", label: "Cost per lead (CPL) only", score: 1 },
      { key: "C", label: "Consultation bookings per campaign", score: 2 },
      { key: "D", label: "Revenue per procedure traced back to specific campaigns", score: 3 },
    ],
  },
  {
    id: "q5",
    number: "Q05",
    text: "How often do you send your CRM's patient data back to Meta to update its learning?",
    options: [
      { key: "A", label: "I didn't know this was possible", score: 0 },
      { key: "B", label: "We've never done it", score: 0 },
      { key: "C", label: "We've done it once or twice", score: 1 },
      { key: "D", label: "Monthly or more — it's part of our standard workflow", score: 3 },
    ],
  },
  {
    id: "q8",
    number: "Q06",
    text: "After a patient books a consultation but doesn't show up — what changes in your Meta setup?",
    options: [
      { key: "A", label: "Nothing changes on the ad side", score: 0 },
      { key: "B", label: "We pause the ad that brought them", score: 1 },
      { key: "C", label: "We note it internally — Meta still targets similar profiles", score: 1 },
      { key: "D", label: "Their profile is sent back to Meta as a negative signal", score: 3 },
    ],
  },
  {
    id: "q10",
    number: "Q07",
    text: "Has your agency ever connected your CRM to your Meta ad account?",
    options: [
      { key: "A", label: "We don't use a CRM", score: 0 },
      { key: "B", label: "No — they're completely separate", score: 0 },
      { key: "C", label: "Partially — some basic data is connected", score: 1 },
      { key: "D", label: "Yes — CRM stage events fire as custom conversion events in Meta", score: 3 },
    ],
  },
];

export const MAX_SCORE = 21;

// Result band definitions ------------------------------------
export const BANDS = [
  {
    id: "emergency",
    min: 0,
    max: 3,
    name: "Signal Emergency",
    // pill palette
    pillBg: "rgba(220,38,38,0.15)",
    pillText: "#f87171",
    pillBorder: "rgba(220,38,38,0.3)",
    headline: "Your ads are training themselves to fail — every single month.",
    body:
      "Right now Meta has no data on what a real paying patient looks like at your brand. Every week this runs, the wrong signal gets more entrenched and harder to correct.",
    cards: [
      {
        label: "Estimated waste",
        value: "60–80%",
        desc: "Proportion of current ad spend going to leads with near-zero conversion probability",
      },
      {
        label: "Algorithm state",
        value: "Misfiring",
        desc: "Meta is reinforcing the wrong signal every week the campaign runs",
      },
      {
        label: "First fix",
        value: "CRM connection",
        desc: "Connecting CRM to Meta's CAPI is the single highest-leverage change available",
      },
    ],
  },
  {
    id: "trap",
    min: 4,
    max: 9,
    name: "Price-Shopper Trap",
    pillBg: "rgba(217,119,6,0.15)",
    pillText: "#fcd34d",
    pillBorder: "rgba(217,119,6,0.3)",
    headline: "Your algorithm is actively finding bargain hunters.",
    body:
      "Your campaigns are optimising for form fills and getting very good at finding them. Every ghost lead and price-checker is teaching Meta that this is your ideal patient.",
    cards: [
      {
        label: "Monthly revenue loss",
        value: "~5×",
        desc: "Estimated gap in paying patients per ₹1L spend vs a revenue-trained setup",
      },
      {
        label: "Algorithm status",
        value: "Degrading",
        desc: "Each month without a feedback loop, patient quality typically declines slightly",
      },
      {
        label: "Urgency level",
        value: "High",
        desc: "The compounding effect means month 6 is worse than month 3",
      },
    ],
  },
  {
    id: "gap",
    min: 10,
    max: 16,
    name: "Quality Gap",
    pillBg: "rgba(8,145,178,0.15)",
    pillText: "#67e8f9",
    pillBorder: "rgba(8,145,178,0.3)",
    headline: "You're generating leads but losing revenue to the wrong patients.",
    body:
      "You have some of the right infrastructure but the loop isn't fully closed. Meta is still finding a mix of serious patients and price-checkers because it hasn't seen the full revenue picture.",
    cards: [
      {
        label: "Revenue at risk",
        value: "3–4×",
        desc: "Typical improvement in paying patients per ₹1L once the CRM loop is fully closed",
      },
      {
        label: "Your main leak",
        value: "Signal gap",
        desc: "Meta has partial data but not the full revenue picture of who actually paid",
      },
      {
        label: "Time to fix",
        value: "60–90 days",
        desc: "Measurable shift in patient quality within this window once the loop is operational",
      },
    ],
  },
  {
    id: "ready",
    min: 17,
    max: 21,
    name: "Premium Signal Ready",
    pillBg: "rgba(22,163,74,0.15)",
    pillText: "#86efac",
    pillBorder: "rgba(22,163,74,0.3)",
    headline: "Your signal infrastructure is ahead of most clinics.",
    body:
      "You've built the feedback loops most health and wellness brands don't know exist. The opportunity now is refinement — tightening your seed audiences and suppression logic.",
    cards: [
      {
        label: "Your signal tier",
        value: "Top 10%",
        desc: "Among clinics running paid ads in the aesthetic and premium health space",
      },
      {
        label: "Likely bottleneck",
        value: "Refinement",
        desc: "Lookalike seed quality and creative-to-revenue attribution are the next levers",
      },
      {
        label: "Recommended next step",
        value: "Audit",
        desc: "Review seed audience freshness and suppression audience coverage",
      },
    ],
  },
];

export function getBand(score) {
  return BANDS.find((b) => score >= b.min && score <= b.max) || BANDS[0];
}

// Framework pillars ------------------------------------------
export const PILLARS = [
  {
    label: "PILLAR 01 — REVENUE TRAINING",
    title: "Train the algorithm on patients who paid, not patients who enquired",
    body:
      "Every CRM milestone — consultation completed, procedure booked, full price paid — fires back to Meta as a custom conversion event with a tiered value. The algorithm stops optimising for form fills and starts learning what a real buyer looks like for your specific procedure mix. Over 90 days, the patient profile it finds shifts measurably.",
  },
  {
    label: "PILLAR 02 — ACTIVE SUPPRESSION",
    title: "Stop paying to show ads to people who already told you they won't buy",
    body:
      "Every ghost lead, every no-show, every price-checker who asked for a discount and disappeared gets added to a suppression audience. Meta stops targeting them. More importantly, they stop being used as signals that tell Meta who to find next. Your budget follows buyers, not dead ends.",
  },
  {
    label: "PILLAR 03 — HIGH-LTV LOOKALIKES",
    title: "Build your lookalike from your best patients, not your entire list",
    body:
      "The seed audience is filtered: patients who paid full price for high-ticket procedures within the last 90–180 days. A 1% lookalike from this seed finds fundamentally different people than a lookalike from all website visitors. This is the single change that shifts the income profile of incoming enquiries most visibly.",
  },
];

// Benchmarks -------------------------------------------------
export const BENCHMARKS = [
  {
    number: "19%",
    label: "Lower cost per quality lead with CAPI and CRM data connected to Meta",
    source: "Meta Official · LeadSync 2026",
  },
  {
    number: "5×",
    label: "More paying patients per ₹1L spend when optimising on revenue events vs form fills",
    source: "RTD Framework Analysis",
  },
  {
    number: "25–35%",
    label: "New-patient consultation no-show rate for price-driven Meta campaigns",
    source: "ProspyrMed, 2025",
  },
  {
    number: "8–12%",
    label: "No-show rate for clinics running revenue-trained campaigns",
    source: "ProspyrMed, 2025",
  },
  {
    number: "1,000",
    label: "High-LTV buyers as a seed outperforms 50,000 general visitors for lookalike quality",
    source: "Meta · Jetfuel 2026",
  },
  {
    number: "90 days",
    label: "Typical time to see measurable shift in patient quality after closing the CRM feedback loop",
    source: "RTD Framework Analysis",
  },
];
